/**
 * Tree Schema — Elementor-style hierarchical layout for the Visual
 * Builder. Introduces a nested Section → Row → Column → Widget schema
 * that lives at `blob.tree` alongside (not instead of) the legacy flat
 * schema. `blob.schema_version` marks the migration.
 *
 * Node shapes:
 *   Section: { id, type:"section", label, hidden, locked, settings:{ bg, padding_x, padding_y, radius }, children:[Row] }
 *   Row:     { id, type:"row",     label, hidden, locked, settings:{ gap, align, wrap }, children:[Column] }
 *   Column:  { id, type:"col",     label, hidden, locked, span, settings:{ align, padding, bg }, children:[Widget] }
 *   Widget:  { id, type:"widget",  label, hidden, locked, comp, settings:{ ...component-specific safe UI keys } }
 *
 * `span` is a 12-column grid unit (1..12). Multiple columns in a row
 * whose spans sum to 12 render as a fully-filled multi-column layout.
 * Sums < 12 leave whitespace on the right; sums > 12 wrap to the next
 * row (with `wrap:"wrap"`).
 *
 * SAFETY: All ops are pure/immutable. No business logic keys are
 * accepted anywhere in the tree — the customer renderer only touches
 * layout, spacing, and typography.
 */

export const SCHEMA_VERSION = 2;

let seq = 0;
const nid = (prefix) => {
  seq += 1;
  return `${prefix}-${Date.now().toString(36)}-${seq.toString(36)}`;
};

const clone = (v) => (v == null ? v : JSON.parse(JSON.stringify(v)));

/* ─── Factories ───────────────────────────────────────────────────── */

export function makeSection(label = "Section") {
  return {
    id: nid("sec"),
    type: "section",
    label,
    hidden: false, locked: false,
    settings: { bg: "", padding_x: 24, padding_y: 32, radius: 0, max_width: "" },
    children: [makeRow()],
  };
}

export function makeRow(label = "Row") {
  return {
    id: nid("row"),
    type: "row",
    label,
    hidden: false, locked: false,
    settings: { gap: 16, align: "stretch", wrap: "wrap" },
    children: [makeColumn(12)],
  };
}

export function makeColumn(span = 6, label = "Column") {
  return {
    id: nid("col"),
    type: "col",
    label,
    hidden: false, locked: false,
    span: Math.max(1, Math.min(12, span)),
    settings: { align: "start", padding: 8, bg: "" },
    children: [],
  };
}

export function makeWidget(comp = "text", label = "") {
  return {
    id: nid("w"),
    type: "widget",
    label: label || comp,
    hidden: false, locked: false,
    comp,
    settings: {},
  };
}

/* ─── Immutable tree ops ──────────────────────────────────────────── */

export function findNode(tree, id, parentId = null, index = -1) {
  for (let i = 0; i < tree.length; i += 1) {
    const n = tree[i];
    if (n.id === id) return { node: n, parentId, index: i };
    if (n.children && n.children.length) {
      const hit = findNode(n.children, id, n.id, i);
      if (hit) return hit;
    }
  }
  return null;
}

// Depth-first walk with (node, ancestors) callback.
export function walkTree(tree, cb, ancestors = []) {
  for (const n of tree) {
    cb(n, ancestors);
    if (n.children) walkTree(n.children, cb, [...ancestors, n]);
  }
}

/** Map a tree returning a NEW tree with `fn(node)` applied to every node. */
function mapTree(tree, fn) {
  return tree.map((n) => {
    const nn = fn(n) || n;
    if (nn.children) return { ...nn, children: mapTree(nn.children, fn) };
    return nn;
  });
}

export function updateNode(tree, id, patch) {
  return mapTree(tree, (n) => {
    if (n.id !== id) return n;
    if (typeof patch === "function") return { ...n, ...patch(n) };
    return { ...n, ...patch };
  });
}

export function updateNodeSettings(tree, id, settingsPatch) {
  return mapTree(tree, (n) => {
    if (n.id !== id) return n;
    return { ...n, settings: { ...(n.settings || {}), ...settingsPatch } };
  });
}

/** Remove the node with the given id — safe if the node is not found. */
export function removeNode(tree, id) {
  return tree
    .filter((n) => n.id !== id)
    .map((n) => (n.children ? { ...n, children: removeNode(n.children, id) } : n));
}

/** Insert `newNode` as the first/last child of `parentId`. */
export function insertChild(tree, parentId, newNode, position = "last") {
  if (!parentId) {
    return position === "first" ? [newNode, ...tree] : [...tree, newNode];
  }
  return mapTree(tree, (n) => {
    if (n.id !== parentId) return n;
    const kids = n.children || [];
    return { ...n, children: position === "first" ? [newNode, ...kids] : [...kids, newNode] };
  });
}

/** Insert `newNode` before/after the sibling `siblingId`. */
export function insertBeside(tree, siblingId, newNode, position = "after") {
  const walk = (list) => {
    let out = [];
    let inserted = false;
    for (const n of list) {
      if (n.id === siblingId) {
        if (position === "before") out.push(newNode);
        out.push(n.children ? { ...n, children: walk(n.children) } : n);
        if (position === "after") out.push(newNode);
        inserted = true;
      } else {
        out.push(n.children ? { ...n, children: walk(n.children) } : n);
      }
    }
    return out;
  };
  return walk(tree);
}

/** Duplicate the node with the given id — the copy sits right after it. */
export function duplicateNode(tree, id) {
  const hit = findNode(tree, id);
  if (!hit) return tree;
  const stamped = stampIds(clone(hit.node));
  stamped.label = `${stamped.label || stamped.type} (copy)`;
  return insertBeside(tree, id, stamped, "after");
}

function stampIds(node) {
  node.id = nid(node.type || "n");
  if (node.children) node.children = node.children.map(stampIds);
  return node;
}

/**
 * Move node `id` to `newParentId` (or top-level if null) at `newIndex`
 * (0-based). Handles moving deeper/shallower/same-level cleanly.
 */
export function moveNode(tree, id, newParentId, newIndex) {
  const hit = findNode(tree, id);
  if (!hit) return tree;
  const source = clone(hit.node);
  let without = removeNode(tree, id);
  // Insert into new parent.
  if (!newParentId) {
    const nextTop = [...without];
    const clamped = Math.max(0, Math.min(nextTop.length, newIndex));
    nextTop.splice(clamped, 0, source);
    return nextTop;
  }
  return mapTree(without, (n) => {
    if (n.id !== newParentId) return n;
    const kids = n.children ? [...n.children] : [];
    const clamped = Math.max(0, Math.min(kids.length, newIndex));
    kids.splice(clamped, 0, source);
    return { ...n, children: kids };
  });
}

/**
 * Reorder within the same parent by moving `id` to `newIndex` among its
 * siblings. Uses `findNode` to locate the sibling list.
 */
export function reorderSibling(tree, id, newIndex) {
  const hit = findNode(tree, id);
  if (!hit) return tree;
  return moveNode(tree, id, hit.parentId, newIndex);
}

/* ─── Migration ───────────────────────────────────────────────────── */

/**
 * Lift a flat `blob.sections` list into a nested `blob.tree`.
 * Each flat widget becomes:  Section (1 row × 1 col × widget).
 * Safe to call multiple times — if `blob.tree` already exists it is
 * returned untouched.
 *
 * Also stamps `blob.schema_version = 2`.
 */
export function migrateBlobToTree(blob) {
  const b = { ...(blob || {}) };
  if (Array.isArray(b.tree) && b.tree.length > 0) {
    b.schema_version = Math.max(b.schema_version || 0, SCHEMA_VERSION);
    return b;
  }
  const flat = Array.isArray(b.sections) ? b.sections : [];
  const tree = flat.map((s, idx) => {
    const sec = makeSection(s.label || s.comp || `Section ${idx + 1}`);
    const row = sec.children[0];
    const col = row.children[0];
    col.span = 12;
    const w = makeWidget(s.comp || "text", s.label || s.comp);
    // Copy any settings the flat schema had into the widget so the
    // migration is byte-safe.
    if (b.components && b.components[s.comp]) {
      w.settings = { ...(b.components[s.comp] || {}) };
    }
    col.children = [w];
    return sec;
  });
  b.tree = tree;
  b.schema_version = SCHEMA_VERSION;
  return b;
}

/** True when the tree is empty (or only contains one auto-migrated placeholder). */
export function isTreeEmpty(tree) {
  if (!Array.isArray(tree) || tree.length === 0) return true;
  return tree.every((sec) => {
    if (sec.hidden) return false;
    const kids = sec.children || [];
    if (kids.length === 0) return true;
    return kids.every((r) => (r.children || []).every((c) => (c.children || []).length === 0));
  });
}

/* ─── Type guards ─────────────────────────────────────────────────── */

export const isSection = (n) => n?.type === "section";
export const isRow     = (n) => n?.type === "row";
export const isColumn  = (n) => n?.type === "col";
export const isWidget  = (n) => n?.type === "widget";

/**
 * Compute the effective grid-template-columns string for a row given
 * its children's spans. Cells wider than 12 wrap to the next line.
 */
export function rowTemplate(row) {
  const cols = row?.children || [];
  if (cols.length === 0) return "1fr";
  return cols.map((c) => `${Math.max(1, Math.min(12, c.span || 12))}fr`).join(" ");
}
